/**
 * whenVisible — wrap a polling callback so it does nothing while the tab is hidden.
 *
 * 🚨 WHY THIS EXISTS, with the measurement. On 2026-08-15 the site-wide chat dock was found polling
 * /api/chat every 8s from BACKGROUNDED tabs: 9 requests in a controlled 62s window on a tab
 * reporting `visibilityState: "hidden"`, i.e. 522 requests/hour/tab, each a force-dynamic query,
 * feeding a badge nobody could see. Fixing it exposed the same fault in 17 page-level pollers.
 *
 * ⭐ WHY A WRAPPER AND NOT A HOOK. The obvious move is a `useVisiblePoll` hook, and it was rejected
 * on purpose. The existing call sites are uniform in shape but NOT in their surroundings: several
 * carry their own early-out guards (`if (!visible || dismissed) return`), extra cleanup (`alive =
 * false`), and hand-tuned dep arrays. Converting them to a hook means restructuring 17 effects and
 * silently re-deciding each of those, which is how a small perf win becomes a regression nobody can
 * bisect. This wraps the CALLBACK ONLY, so every guard, cleanup and dep stays exactly as written and
 * the diff is one token per interval.
 *
 * ⚠ NO RESUME-ON-VISIBLE HERE, DELIBERATELY. The worst case is that data is up to one interval stale
 * when you return to the tab, and these intervals ARE the freshness the component already promises
 * (15-60s). A resume would need a second effect per component, evaluated against guards this helper
 * cannot see. The two site-wide pollers that genuinely needed instant freshness (AlertsChatDock,
 * WatchlistPane price alerts) got explicit visibilitychange handlers instead.
 *
 * ⚠ ONLY FOR NETWORK WORK. Local timers were deliberately left unwrapped: an ET clock, "time ago"
 * tickers, a game counter. They make no request, they drive visible readouts that must stay
 * responsive, and browsers already throttle hidden-tab timers to roughly once a minute. A 1s
 * interval is not automatically a bug.
 *
 * SSR-safe: `document` is undefined on the server, and there we simply run the callback.
 */
export function whenVisible(fn: () => void): () => void {
  return () => {
    if (typeof document !== 'undefined' && document.hidden) return
    fn()
  }
}
